/**
 * m3 echo Frontend - Layer5 Benchmark Detail Dialog
 *
 * Shows side-by-side comparison of Layer 5 analysis
 * without vs with Layer 1 (LLM scoring) context.
 */

import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  Paper,
  Grid,
  Chip,
  IconButton,
  useMediaQuery,
  useTheme,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Divider,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import RemoveIcon from '@mui/icons-material/Remove';
import { Layer5BenchmarkResult } from '../../services/admin';

// Category labels mapping
const CATEGORY_LABELS: Record<string, string> = {
  entity: 'K1: Entity-Klarheit',
  content: 'K2: Content-Qualität',
  reputation: 'K3: Reputation',
  usp: 'K4: USP & Differenzierung',
  local: 'K5: Lokale Relevanz',
  thirdparty: 'K6: Drittquellen',
  technical: 'K7: Technische Signale',
};

const CATEGORY_IDS = ['entity', 'content', 'reputation', 'usp', 'local', 'thirdparty', 'technical'];

interface Props {
  open: boolean;
  onClose: () => void;
  result: Layer5BenchmarkResult | null;
}

const getScoreColor = (score: number): string => {
  if (score >= 90) return '#4caf50';
  if (score >= 75) return '#8bc34a';
  if (score >= 60) return '#ff9800';
  if (score >= 40) return '#f44336';
  return '#d32f2f';
};

const DeltaChip: React.FC<{ delta: number }> = ({ delta }) => {
  if (delta === 0) {
    return <Chip icon={<RemoveIcon />} label="0" size="small" variant="outlined" />;
  }
  const isPositive = delta > 0;
  return (
    <Chip
      icon={isPositive ? <ArrowUpwardIcon /> : <ArrowDownwardIcon />}
      label={`${isPositive ? '+' : ''}${delta}`}
      size="small"
      color={isPositive ? 'success' : 'error'}
      variant="outlined"
    />
  );
};

const Layer5BenchmarkDetailDialog: React.FC<Props> = ({ open, onClose, result }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [expandedCategory, setExpandedCategory] = useState<string | false>(false);

  if (!result) return null;

  const generic = result.result_generic || {};
  const withLayer1 = result.result_with_layer1 || {};
  const scoreGeneric = result.overall_score_generic ?? 0;
  const scoreWithLayer1 = result.overall_score_with_layer1 ?? 0;
  const scoreDelta = Math.round(scoreWithLayer1 - scoreGeneric);

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="lg"
      fullWidth
      fullScreen={isMobile}
    >
      <DialogTitle>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="h6">
            Layer5 Benchmark: {result.project_name}
          </Typography>
          <IconButton onClick={onClose} size="small">
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent dividers>
        {/* Overall Score Header */}
        <Paper sx={{ p: 2, mb: 3, bgcolor: 'grey.50' }}>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} md={4} sx={{ textAlign: 'center' }}>
              <Typography variant="caption" color="text.secondary">
                Ohne LLM-Kontext
              </Typography>
              <Typography variant="h4" sx={{ color: getScoreColor(scoreGeneric), fontWeight: 'bold' }}>
                {Math.round(scoreGeneric)}
              </Typography>
              {result.latency_generic_ms && (
                <Typography variant="caption" color="text.secondary">
                  {(result.latency_generic_ms / 1000).toFixed(1)}s
                </Typography>
              )}
            </Grid>
            <Grid item xs={12} md={4} sx={{ textAlign: 'center' }}>
              <Typography variant="caption" color="text.secondary">
                Delta
              </Typography>
              <Box sx={{ mt: 1 }}>
                <DeltaChip delta={scoreDelta} />
              </Box>
            </Grid>
            <Grid item xs={12} md={4} sx={{ textAlign: 'center' }}>
              <Typography variant="caption" color="text.secondary">
                Mit LLM-Kontext
              </Typography>
              <Typography variant="h4" sx={{ color: getScoreColor(scoreWithLayer1), fontWeight: 'bold' }}>
                {Math.round(scoreWithLayer1)}
              </Typography>
              {result.latency_with_layer1_ms && (
                <Typography variant="caption" color="text.secondary">
                  {(result.latency_with_layer1_ms / 1000).toFixed(1)}s
                </Typography>
              )}
            </Grid>
          </Grid>
        </Paper>

        {/* Category Comparison Table */}
        <Typography variant="subtitle1" gutterBottom>
          Kategorie-Vergleich
        </Typography>
        <TableContainer component={Paper} variant="outlined" sx={{ mb: 3 }}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Kategorie</TableCell>
                <TableCell align="center">Ohne LLM</TableCell>
                <TableCell align="center">Mit LLM</TableCell>
                <TableCell align="center">Delta</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {CATEGORY_IDS.map((catId) => {
                const genScore = generic[catId]?.score ?? '-';
                const l1Score = withLayer1[catId]?.score ?? '-';
                const delta = typeof genScore === 'number' && typeof l1Score === 'number'
                  ? l1Score - genScore
                  : 0;
                return (
                  <TableRow key={catId} hover>
                    <TableCell>{CATEGORY_LABELS[catId] || catId}</TableCell>
                    <TableCell align="center">
                      {typeof genScore === 'number' ? (
                        <Chip
                          label={genScore}
                          size="small"
                          sx={{ bgcolor: getScoreColor(genScore), color: 'white', fontWeight: 'bold' }}
                        />
                      ) : '-'}
                    </TableCell>
                    <TableCell align="center">
                      {typeof l1Score === 'number' ? (
                        <Chip
                          label={l1Score}
                          size="small"
                          sx={{ bgcolor: getScoreColor(l1Score), color: 'white', fontWeight: 'bold' }}
                        />
                      ) : '-'}
                    </TableCell>
                    <TableCell align="center">
                      <DeltaChip delta={delta} />
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>

        {/* Category Detail Accordions */}
        <Typography variant="subtitle1" gutterBottom>
          Detailvergleich
        </Typography>
        {CATEGORY_IDS.map((catId) => {
          const genCat = generic[catId] || {};
          const l1Cat = withLayer1[catId] || {};
          return (
            <Accordion
              key={catId}
              expanded={expandedCategory === catId}
              onChange={(_, isExpanded) => setExpandedCategory(isExpanded ? catId : false)}
              disableGutters
            >
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '100%' }}>
                  <Typography sx={{ flexGrow: 1 }}>
                    {CATEGORY_LABELS[catId] || catId}
                  </Typography>
                  <Chip
                    label={genCat.score ?? '-'}
                    size="small"
                    sx={{
                      bgcolor: typeof genCat.score === 'number' ? getScoreColor(genCat.score) : undefined,
                      color: typeof genCat.score === 'number' ? 'white' : undefined,
                      minWidth: 40,
                    }}
                  />
                  <Typography variant="body2" color="text.secondary">vs</Typography>
                  <Chip
                    label={l1Cat.score ?? '-'}
                    size="small"
                    sx={{
                      bgcolor: typeof l1Cat.score === 'number' ? getScoreColor(l1Cat.score) : undefined,
                      color: typeof l1Cat.score === 'number' ? 'white' : undefined,
                      minWidth: 40,
                    }}
                  />
                </Box>
              </AccordionSummary>
              <AccordionDetails>
                <Grid container spacing={2}>
                  {/* Generic analysis */}
                  <Grid item xs={12} md={6}>
                    <Paper variant="outlined" sx={{ p: 2 }}>
                      <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                        Ohne LLM-Kontext
                      </Typography>
                      <Typography variant="body2" sx={{ mb: 1, whiteSpace: 'pre-wrap' }}>
                        {genCat.analysis || 'Keine Analyse verfügbar'}
                      </Typography>
                      {genCat.recommendations?.length > 0 && (
                        <>
                          <Divider sx={{ my: 1 }} />
                          <Typography variant="caption" color="text.secondary">
                            Empfehlungen:
                          </Typography>
                          {genCat.recommendations.map((rec: any, i: number) => (
                            <Typography key={i} variant="body2" sx={{ ml: 1, mt: 0.5 }}>
                              {rec.priority ? `P${rec.priority}: ` : ''}{rec.title}
                            </Typography>
                          ))}
                        </>
                      )}
                    </Paper>
                  </Grid>
                  {/* With Layer 1 analysis */}
                  <Grid item xs={12} md={6}>
                    <Paper variant="outlined" sx={{ p: 2, borderColor: 'primary.main' }}>
                      <Typography variant="subtitle2" color="primary" gutterBottom>
                        Mit LLM-Kontext
                      </Typography>
                      <Typography variant="body2" sx={{ mb: 1, whiteSpace: 'pre-wrap' }}>
                        {l1Cat.analysis || 'Keine Analyse verfügbar'}
                      </Typography>
                      {l1Cat.recommendations?.length > 0 && (
                        <>
                          <Divider sx={{ my: 1 }} />
                          <Typography variant="caption" color="text.secondary">
                            Empfehlungen:
                          </Typography>
                          {l1Cat.recommendations.map((rec: any, i: number) => (
                            <Typography key={i} variant="body2" sx={{ ml: 1, mt: 0.5 }}>
                              {rec.priority ? `P${rec.priority}: ` : ''}{rec.title}
                            </Typography>
                          ))}
                        </>
                      )}
                    </Paper>
                  </Grid>
                </Grid>
              </AccordionDetails>
            </Accordion>
          );
        })}

        {/* Layer 1 Context (collapsible) */}
        {result.layer1_context && (
          <Accordion sx={{ mt: 3 }} disableGutters>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Typography variant="subtitle2">
                Injizierter Layer 1 Kontext
              </Typography>
            </AccordionSummary>
            <AccordionDetails>
              <Paper variant="outlined" sx={{ p: 2, bgcolor: 'grey.50' }}>
                <Typography
                  variant="body2"
                  sx={{ whiteSpace: 'pre-wrap', fontFamily: 'monospace', fontSize: '0.8rem' }}
                >
                  {result.layer1_context}
                </Typography>
              </Paper>
            </AccordionDetails>
          </Accordion>
        )}

        {!result.layer1_context && (
          <Paper sx={{ p: 2, mt: 3, bgcolor: 'warning.light' }}>
            <Typography variant="body2">
              Kein Layer 1 Kontext verfügbar. Beide Analysen wurden ohne LLM-Scoring-Daten durchgeführt.
            </Typography>
          </Paper>
        )}
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose}>Schließen</Button>
      </DialogActions>
    </Dialog>
  );
};

export default Layer5BenchmarkDetailDialog;
